import React from 'react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import FairList from './FairList';
import FairCalendar from './FairCalendar';
import Welcome from './Welcome';

/**
 * Configurable Landing Page Component
 *
 * Dynamically renders the homepage based on the 'landing_page_mode' feature flag.
 *
 * Modes:
 * - 'calendar': Interactive calendar view showing upcoming fairs (RECOMMENDED DEFAULT)
 * - 'listing': Searchable/filterable list of all fairs
 * - 'marketing': Traditional marketing page with hero, features, testimonials
 *
 * Benefits of calendar as default:
 * - Immediate value: Users see actual event dates right away
 * - Time-sensitive: Creates urgency and excitement
 * - Visual engagement: Calendar grid is more engaging than list
 * - Mobile friendly: Calendar is already optimized for mobile
 * - SEO: Event data (dates, locations) visible on homepage
 *
 * Issue #1513: Configurable landing page with calendar option
 * Related: Issue #1511 (branding consistency)
 */
export default function ConfigurableLandingPage() {
  // Get landing page mode from feature flag (default to 'listing')
  const { value: landingPageMode, isLoading } = useFeatureFlag('landing_page_mode');

  // Default to 'listing' if flag is not set or still loading
  const mode = isLoading ? 'listing' : (landingPageMode || 'listing');

  // Render appropriate component based on mode
  // Note: FairList has its own <main> wrapper, FairCalendar and Welcome need wrapping
  switch (mode) {
    case 'calendar':
      return (
        <main className="flex-grow overflow-y-auto">
          <FairCalendar />
        </main>
      );

    case 'listing':
      return <FairList />;

    case 'marketing':
      return (
        <main className="flex-grow overflow-y-auto">
          <Welcome />
        </main>
      );

    default:
      // Fallback to listing if invalid mode
      return <FairList />;
  }
}
